import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { screensColumns } from './screens-table-component/columns/columns.tsx';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import ScreensDialogForm from '../screens-dialog/screens-dialog-form.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import useScreensStore from '../../store/screens.store.ts';
import { ICONS } from '@shared/components/icons/icon.tsx';
import {
  DigitalSignageScreenReqModel,
  DigitalSignageScreensModel
} from '@modules/digital-signage/model/screens.model.ts';
import { getDeviceScreens } from '@shared/api/digital-signage/screens-api.ts';
import { useSearchParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import ViewControls from '@components/view-controls/view-controls.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { SortOrder, TriggerTypeEnum } from '@models/table.model.ts';
import ScreenPreviewModal from '@modules/digital-signage/pages/device-screens/compnents/screen-preview-modal/screen-preview-modal.tsx';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { getRowIdKey } from '@utils/table-selection.utils.tsx';

const ScreensTable: React.FC = () => {
  const { deleteRow, isLoading, editRow, createRow } = useScreensStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedKeys: selectedRows,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<DigitalSignageScreensModel>({
    tableStore,
    getRowKey: getRowIdKey
  });
  const [view, setView] = useState(ViewControlsEnum.List);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedItem, setSelectedItem] =
    useState<DigitalSignageScreensModel | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const [openScreenPreviewModal, setOpenScreenPreviewModal] =
    React.useState<boolean>(false);

  const count = selectedRows.length;

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItemId(row?._id || null);
  };

  const handleDeleteConfirm = () => {
    deleteRow({
      id: selectedItemId ? selectedItemId : selectedRows,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleSave = (data: DigitalSignageScreenReqModel) => {
    if (selectedItem?._id) {
      editRow({
        data,
        id: selectedItem?._id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createRow({ data, state: tableStore.getState(), handleClose });
    }
  };

  const handleClose = () => {
    resetAllSelectedMedia();
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setSelectedItem(null);
    setSelectedItemId('');
    clearSelection();
  };

  const handleClosePreview = () => {
    resetAllSelectedMedia();
    setOpenScreenPreviewModal(false);
    setSelectedItem(null);
    setSelectedItemId('');
    clearSelection();
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
    }
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const handlePreview = (row: any) => {
    if (row?.media || row.mediaUrl) {
      setOpenScreenPreviewModal(true);
      setSelectedItem(row);
    }
  };

  const columns = screensColumns(handleEdit, handleDelete, handlePreview, {
    handleSelectRow,
    handleSelectAll,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected
  });

  useEffect(() => {
    const dialog = searchParams.get('dialog');
    if (dialog === 'open') {
      setOpenDialog(true);
    }
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls
          showDelete={true}
          showLanguageSelect={false}
          onAction={handleAction}
          selectedRows={selectedRows}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getDeviceScreens}
            onTriggerAction={handleTableTrigger}
            columns={columns}
            tableKey="digital-signage-screens-table"
            initialState={{
              sort: ['createdAt', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          title={t('digital_signage_add_screens_title')}
          size={isTabletScreen ? 'sm' : 'md'}
          content={
            <ScreensDialogForm
              selectedItem={selectedItem}
              onSave={handleSave}
            />
          }
          onClose={handleClose}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          isLoading={isLoading()}
          open={showDeleteDialog}
          title={
            selectedItemId
              ? t('delete_popup_title')
              : t('delete_popup_title_many_items', { count })
          }
        />
      </div>

      {openScreenPreviewModal && (
        <ScreenPreviewModal data={selectedItem} onClose={handleClosePreview} />
      )}
    </div>
  );
};

export default ScreensTable;
